import React from "react";
import {
  Box,
  Chip,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress
} from "@mui/material";
import { useUserBudgetStatus } from "./hooks/useUserBudgetStatus";
import { useUserCategoriesWithRules } from "./hooks/useUserCategoriesWithRules";

const StatusChip = ({ hasLimit, hasEntry }) => {
  if (!hasEntry) {
    return <Chip label="Not budgeted" size="small" />;
  }
  return hasLimit
    ? <Chip label="Set" size="small" color="success" />
    : <Chip label="No cap" size="small" color="default" />;
};

const CategoryStatusChip = ({ limit }) => {
  if (!limit) {
    return <Chip label="None" size="small" />;
  }
  return limit.hasLimit
    ? <Chip label="Set" size="small" color="success" />
    : <Chip label="No cap" size="small" color="default" />;
};

const BudgetSummary = ({ budget }) => {
  const { currentPeriod } = budget;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
      <Typography variant="h6">{budget.name}</Typography>
      <StatusChip
        hasEntry={!!currentPeriod}
        hasLimit={currentPeriod?.hasLimit}
      />
      {currentPeriod && (
        <Typography variant="body2" color="textSecondary">
          Effective {currentPeriod.effectiveFrom}
        </Typography>
      )}
    </Box>
  );
};

const CategoryLimitsTable = ({ budget, categories }) => {
  const limitsByCategoryId = {};
  for (const limit of budget.categoryLimits) {
    limitsByCategoryId[limit.userCategoryId] = limit;
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Category</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Effective From</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {categories.map((category) => {
          const limit = limitsByCategoryId[category.id];
          return (
            <TableRow key={category.id}>
              <TableCell>{category.name}</TableCell>
              <TableCell>
                <CategoryStatusChip limit={limit} />
              </TableCell>
              <TableCell>{limit?.effectiveFrom || "—"}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

export const BudgetStatusSection = ({ userId }) => {
  const { data: budgetData, isLoading: isBudgetLoading } =
    useUserBudgetStatus(userId);
  const { data: categoryData, isLoading: isCategoryLoading } =
    useUserCategoriesWithRules(userId);

  if (isBudgetLoading || isCategoryLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const budgets = budgetData?.budgets ?? [];
  const categories = categoryData?.userCategories ?? [];

  if (budgets.length === 0) {
    return (
      <Typography color="textSecondary">
        No budget configured for this user.
      </Typography>
    );
  }

  return (
    <Box>
      {budgets.map((budget) => (
        <Box key={budget.id} sx={{ mb: 4 }}>
          <BudgetSummary budget={budget} />
          <CategoryLimitsTable budget={budget} categories={categories} />
        </Box>
      ))}
    </Box>
  );
};
